// src/pages/sales/ReporteVentas.js
// Página de reportes contables de ventas por método de pago y categoría

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Form, Alert, Pagination } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { getSaleReportSummary } from '../../services/saleApi';
import { getCategories } from '../../services/categoryApi';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import './ReporteVentas.css';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const formatterCOPDecimal = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const meses = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const IVA_FILTER_ALL = 'all';
const IVA_FILTER_5 = 'iva5';
const IVA_FILTER_19 = 'iva19';
const IVA_FILTER_EXENTOS = 'exentos';

const getIvaType = (category) => {
  const iva = category?.iva;
  if (!iva) return 'exentos';
  const rate = iva.rate;
  if (rate === 5 || rate === 0.05) return 'iva5';
  if (rate === 19 || rate === 0.19) return 'iva19';
  return 'exentos';
};

const ReporteVentas = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedIvaFilter, setSelectedIvaFilter] = useState('all'); // 'all' | 'iva5' | 'iva19' | 'exentos'
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Opciones de filtros
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [categories, setCategories] = useState([]);

  // Resumen por método de pago (general del mes, sin paginación)
  const [summaryData, setSummaryData] = useState([]);

  // Cargar opciones de filtros
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // Cargar métodos de pago
        try {
          const res = await dotoniwoofApi.get('/paymentMethod.php/sales');
          if (res.data?.code === 200) {
            setPaymentMethods(res.data.message || []);
          }
        } catch {
          try {
            const res = await dotoniwoofApi.get('/paymentMethod.php');
            if (res.data?.code === 200) {
              setPaymentMethods(res.data.message || []);
            }
          } catch (e) {
            console.error('Error cargando métodos de pago:', e);
          }
        }

        // Cargar categorías
        const catRes = await getCategories();
        if (catRes.ok && catRes.data?.code === 200) {
          setCategories(catRes.data.message || []);
        }
      } catch (error) {
        console.error('Error cargando opciones:', error);
      }
    };
    loadOptions();
  }, []);

  // Cargar resumen (general del mes, sin paginación)
  const loadReport = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const summaryRes = await getSaleReportSummary(
        selectedYear,
        selectedMonth,
        selectedPaymentMethods,
        selectedCategories
      );
      if (summaryRes.ok && Array.isArray(summaryRes.summary)) {
        setSummaryData(summaryRes.summary);
      } else {
        setSummaryData([]);
      }
    } catch (error) {
      console.error('Error cargando reporte:', error);
      setErrorMsg('Error al cargar el reporte. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedPaymentMethods, selectedCategories]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleYearChange = (e) => setSelectedYear(parseInt(e.target.value));
  const handleMonthChange = (e) => setSelectedMonth(parseInt(e.target.value));

  const handlePaymentMethodChange = (e) => {
    const value = parseInt(e.target.value);
    if (e.target.checked) {
      setSelectedPaymentMethods([...selectedPaymentMethods, value]);
    } else {
      setSelectedPaymentMethods(selectedPaymentMethods.filter(id => id !== value));
    }
  };

  const handleCategoryChange = (e) => {
    const value = parseInt(e.target.value);
    if (e.target.checked) {
      setSelectedCategories([...selectedCategories, value]);
    } else {
      setSelectedCategories(selectedCategories.filter(id => id !== value));
    }
  };

  const handleSelectAllCategories = () => {
    const ids = filteredCategories.map((c) => c.id_category || c.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedCategories.includes(id));
    setSelectedCategories(allSelected ? [] : ids);
  };

  const handleClearFilters = () => {
    setSelectedPaymentMethods([]);
    setSelectedCategories([]);
    setSelectedIvaFilter(IVA_FILTER_ALL);
  };

  const filteredCategories = categories.filter((cat) => {
    if (selectedIvaFilter === IVA_FILTER_ALL) return true;
    return getIvaType(cat) === selectedIvaFilter;
  });

  const handleRefresh = () => loadReport();

  // Calcular totales del resumen
  const totalSummary = summaryData.reduce((sum, method) => sum + (method.total_amount || 0), 0);

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Container fluid className="reporte-ventas-container">
        <div className="reporte-ventas-header">
          <div className="reporte-ventas-title-section">
            <h1 className="reporte-ventas-title">
              <span className="title-icon">📊</span>
              Reporte Contable de Ventas
            </h1>
            <p className="reporte-ventas-subtitle">Análisis por método de pago y categoría</p>
          </div>
          <div className="reporte-ventas-controls">
            <Form.Select
              value={selectedYear}
              onChange={handleYearChange}
              className="reporte-ventas-select"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - i).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              value={selectedMonth}
              onChange={handleMonthChange}
              className="reporte-ventas-select"
            >
              {meses.map((mes, index) => (
                <option key={index + 1} value={index + 1}>
                  {mes}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="primary"
              onClick={handleRefresh}
              disabled={loading}
              className="reporte-ventas-btn-refresh"
            >
              {loading ? <Spinner animation="border" size="sm" /> : '🔄 Actualizar'}
            </Button>
          </div>
        </div>

        {errorMsg && (
          <Alert variant="danger" className="reporte-ventas-alert">
            {errorMsg}
          </Alert>
        )}

        {/* Filtros */}
        <Row className="reporte-ventas-filters-row">
          <Col xs={12} lg={6}>
            <Card className="reporte-ventas-filter-card">
              <Card.Header className="reporte-ventas-card-header">
                <h6 className="reporte-ventas-card-title">
                  <span className="card-title-icon">💳</span>
                  Métodos de Pago
                </h6>
              </Card.Header>
              <Card.Body>
                {paymentMethods.length > 0 ? (
                  <div className="reporte-ventas-checkbox-group">
                    {paymentMethods.map((method) => (
                      <Form.Check
                        key={method.id_payment_method || method.id}
                        type="checkbox"
                        id={`payment-${method.id_payment_method || method.id}`}
                        label={method.method_name || method.payment_method_name || method.name}
                        checked={selectedPaymentMethods.includes(method.id_payment_method || method.id)}
                        onChange={handlePaymentMethodChange}
                        value={method.id_payment_method || method.id}
                        className="reporte-ventas-checkbox"
                      />
                    ))}
                  </div>
                ) : (
                  <p className="reporte-ventas-empty">Cargando métodos de pago...</p>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card className="reporte-ventas-filter-card">
              <Card.Header className="reporte-ventas-card-header">
                <h6 className="reporte-ventas-card-title">
                  <span className="card-title-icon">📁</span>
                  Categorías
                </h6>
              </Card.Header>
              <Card.Body>
                {categories.length > 0 ? (
                  <>
                    <div className="reporte-ventas-iva-filter">
                      <span className="reporte-ventas-iva-filter-label">Tipo IVA:</span>
                      <div className="reporte-ventas-iva-filter-btns">
                        <Button
                          size="sm"
                          variant={selectedIvaFilter === IVA_FILTER_ALL ? 'primary' : 'outline-secondary'}
                          className="reporte-ventas-iva-btn"
                          onClick={() => setSelectedIvaFilter(IVA_FILTER_ALL)}
                        >
                          Todos
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedIvaFilter === IVA_FILTER_5 ? 'primary' : 'outline-secondary'}
                          className="reporte-ventas-iva-btn reporte-ventas-iva-btn--5"
                          onClick={() => setSelectedIvaFilter(IVA_FILTER_5)}
                        >
                          IVA 5%
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedIvaFilter === IVA_FILTER_19 ? 'primary' : 'outline-secondary'}
                          className="reporte-ventas-iva-btn reporte-ventas-iva-btn--19"
                          onClick={() => setSelectedIvaFilter(IVA_FILTER_19)}
                        >
                          IVA 19%
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedIvaFilter === IVA_FILTER_EXENTOS ? 'primary' : 'outline-secondary'}
                          className="reporte-ventas-iva-btn reporte-ventas-iva-btn--exento"
                          onClick={() => setSelectedIvaFilter(IVA_FILTER_EXENTOS)}
                        >
                          Exentos
                        </Button>
                      </div>
                    </div>
                    {filteredCategories.length > 0 && (
                      <Form.Check
                        type="checkbox"
                        id="category-select-all"
                        label={
                          <span className="reporte-ventas-select-all-label">
                            {filteredCategories.every((c) => selectedCategories.includes(c.id_category || c.id))
                              ? 'Deseleccionar todas'
                              : 'Seleccionar todas'}
                          </span>
                        }
                        checked={filteredCategories.length > 0 && filteredCategories.every((c) => selectedCategories.includes(c.id_category || c.id))}
                        onChange={handleSelectAllCategories}
                        className="reporte-ventas-checkbox reporte-ventas-checkbox-select-all"
                      />
                    )}
                    <div className="reporte-ventas-checkbox-group">
                      {filteredCategories.map((category) => {
                        const iva = category.iva;
                        const ivaType = getIvaType(category);
                        const rateDisplay = iva?.description ?? (iva?.rate != null
                          ? (iva.rate < 1 ? `IVA ${(iva.rate * 100).toFixed(1)}%` : `IVA ${iva.rate}%`)
                          : null);
                        const badgeText = rateDisplay ?? 'Exento';
                        const badgeClass = `reporte-ventas-category-rate reporte-ventas-category-rate--${ivaType}`;
                        
                        return (
                          <Form.Check
                            key={category.id_category || category.id}
                            type="checkbox"
                            id={`category-${category.id_category || category.id}`}
                            checked={selectedCategories.includes(category.id_category || category.id)}
                            onChange={handleCategoryChange}
                            value={category.id_category || category.id}
                            className="reporte-ventas-checkbox"
                            label={
                              <div className="reporte-ventas-category-label">
                                <span className="reporte-ventas-category-name">
                                  {category.category_name || category.name}
                                </span>
                                <span className={badgeClass}>
                                  {badgeText}
                                </span>
                              </div>
                            }
                          />
                        );
                      })}
                    </div>
                    {filteredCategories.length === 0 && (
                      <p className="reporte-ventas-empty">No hay categorías con este tipo de IVA</p>
                    )}
                  </>
                ) : (
                  <p className="reporte-ventas-empty">Cargando categorías...</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Botón limpiar filtros */}
        {(selectedPaymentMethods.length > 0 || selectedCategories.length > 0) && (
          <Row className="mb-3">
            <Col xs={12} className="text-center">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleClearFilters}
                className="reporte-ventas-btn-clear"
              >
                🗑️ Limpiar Filtros
              </Button>
            </Col>
          </Row>
        )}

        {/* Resumen por método de pago */}
        {summaryData.length > 0 && (
          <Row className="reporte-ventas-summary-row">
            <Col xs={12}>
              <Card className="reporte-ventas-summary-card">
                <Card.Header className="reporte-ventas-card-header">
                  <h5 className="reporte-ventas-card-title">
                    <span className="card-title-icon">💰</span>
                    Resumen por Método de Pago
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive className="reporte-ventas-summary-table">
                    <thead>
                      <tr>
                        <th>Método de Pago</th>
                        <th>Categoría</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.map((method, methodIndex) => (
                        <React.Fragment key={method.id_payment_method || methodIndex}>
                          {method.categories && method.categories.length > 0 ? (
                            method.categories.map((category, catIndex) => (
                              <tr key={`${method.id_payment_method}-${category.id_category || catIndex}`}>
                                {catIndex === 0 && (
                                  <td rowSpan={method.categories.length} className="reporte-ventas-method-cell">
                                    <strong>{method.method_name}</strong>
                                  </td>
                                )}
                                <td>{category.category_name}</td>
                                <td className="text-end">{formatterCOP.format(category.total_amount || 0)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td><strong>{method.method_name}</strong></td>
                              <td>-</td>
                              <td className="text-end">{formatterCOP.format(method.total_amount || 0)}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="reporte-ventas-summary-total">
                        <td colSpan="2"><strong>Total General</strong></td>
                        <td className="text-end"><strong>{formatterCOP.format(totalSummary)}</strong></td>
                      </tr>
                    </tfoot>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

export default ReporteVentas;
